import * as Sentry from '@sentry/react'
import type { Scope, SeverityLevel } from '@sentry/react'

/** What a payment modal event knows about where it happened; every field is optional. */
export interface PaymentModalTelemetryContext {
  level?: SeverityLevel
  event?: string
  paymentSurface?: string
  paymentMethod?: string
  phase?: string
  retryCount?: number
  threeDsState?: string
  failureCategory?: string | null
  /** The message staff saw. Our own validation text or Stripe's decline text; no card details. */
  failureMessage?: string | null
  /** Stripe's structured error, when Stripe refused the card; read through `stripeErrorFields`. */
  stripeError?: unknown
  issueCode?: string | null
  initializationKind?: string | null
  initializationStage?: string | null
  initializationElapsedMs?: number | null
  initializationMilestonesMs?: Record<string, number> | null
  organizationId?: number | string | null
  cartId?: number | null
  salesOrderId?: number | null
  customerId?: number | null
  terminalCount?: number | null
  paymentModalModuleLoadElapsedMs?: number | null
}

type ReportPaymentModalFailure = (message: string, context?: PaymentModalTelemetryContext) => void

/** Failures worth counting on their own in Sentry, named by what happened. */
export type PaymentModalFailureReason = 'card_declined' | 'balance_refused' | 'fee_changed'

export function paymentModalFailureReason(
  message: string | null | undefined
): PaymentModalFailureReason | null {
  const normalized = (message ?? '').toLowerCase()
  if (normalized.includes('declined')) return 'card_declined'
  if (normalized.includes('cannot exceed the remaining')) return 'balance_refused'
  if (normalized.includes('the fee changed')) return 'fee_changed'

  return null
}

/** A coarse category for the failure staff saw, from its message, for events that don't name one. */
export function paymentModalFailureCategory(message: string): string {
  const normalized = message.toLowerCase().trim()
  if (!normalized) return 'unknown'
  if (normalized.includes('remaining balance')) return 'remaining_balance_validation'
  if (
    normalized.includes('minimum amount') ||
    normalized.includes('card payments must be at least')
  )
    return 'card_minimum_validation'
  if (normalized.includes('prepare this card payment')) return 'card_prepare_failed'
  if (normalized.includes('verify payment confirmation')) return 'payment_confirmation_failed'
  if (normalized.includes('reader')) return 'reader_failure'
  if (normalized.includes('cash') || normalized.includes('check')) return 'manual_payment_failure'

  return 'generic_failure'
}

/** Stripe's error type, code and decline code, from a Stripe.js error or a PaymentIntent's last error. */
export function stripeErrorFields(error: unknown): {
  type: string | null
  code: string | null
  declineCode: string | null
} {
  const read = (key: string): string | null => {
    if (typeof error !== 'object' || error === null || !(key in error)) return null
    const value: unknown = Reflect.get(error, key)

    return typeof value === 'string' && value !== '' ? value : null
  }

  return { type: read('type'), code: read('code'), declineCode: read('decline_code') }
}

/** The level, tags and context one payment modal event carries. */
export function paymentModalScope(context: PaymentModalTelemetryContext = {}): {
  level: SeverityLevel
  tags: Record<string, string>
  context: Record<string, unknown>
} {
  const retryCount = Number(context.retryCount ?? 0)
  const attemptKind = retryCount > 0 ? 'retry' : 'first_attempt'
  const failureMessage = context.failureMessage ? context.failureMessage.slice(0, 300) : null
  const stripe = stripeErrorFields(context.stripeError)
  const failureCategory =
    context.failureCategory ??
    (context.failureMessage == null ? null : paymentModalFailureCategory(context.failureMessage))
  // Stripe's card errors are declines however they're worded ("Your card has insufficient funds.").
  const failureReason =
    stripe.type === 'card_error' ? 'card_declined' : paymentModalFailureReason(failureMessage)
  const optionalTags = {
    payment_modal_event: context.event,
    payment_modal_failure_category: failureCategory,
    payment_modal_failure_reason: failureReason,
    payment_stripe_error_code: stripe.code,
    payment_stripe_decline_code: stripe.declineCode,
    payment_modal_issue_code: context.issueCode,
    payment_initialization_kind: context.initializationKind,
    payment_initialization_stage: context.initializationStage,
  }
  const tags: Record<string, string> = {
    feature_area: 'payment_modal',
    payment_surface: context.paymentSurface ?? 'unknown',
    payment_method: context.paymentMethod ?? 'unknown',
    controller_phase: context.phase ?? 'unknown',
    attempt_kind: attemptKind,
  }
  for (const [key, value] of Object.entries(optionalTags)) {
    if (value) tags[key] = value
  }

  return {
    level: context.level ?? 'warning',
    tags,
    context: {
      paymentSurface: context.paymentSurface ?? 'unknown',
      paymentMethod: context.paymentMethod ?? 'unknown',
      phase: context.phase ?? 'unknown',
      retryCount,
      attemptKind,
      threeDsState: context.threeDsState ?? 'unknown',
      failureCategory,
      failureMessage,
      failureReason,
      stripeErrorType: stripe.type,
      stripeErrorCode: stripe.code,
      stripeDeclineCode: stripe.declineCode,
      issueCode: context.issueCode ?? null,
      initializationKind: context.initializationKind ?? null,
      initializationStage: context.initializationStage ?? null,
      initializationElapsedMs: context.initializationElapsedMs ?? null,
      initializationMilestonesMs: context.initializationMilestonesMs ?? null,
      organizationId: context.organizationId ?? null,
      cartId: context.cartId ?? null,
      salesOrderId: context.salesOrderId ?? null,
      customerId: context.customerId ?? null,
      terminalCount: context.terminalCount ?? null,
      paymentModalModuleLoadElapsedMs: context.paymentModalModuleLoadElapsedMs ?? null,
    },
  }
}

export const PAYMENT_INITIALIZATION_SLOW_THRESHOLD_MS = 10_000

function shouldSkipTelemetry() {
  return import.meta.env.MODE === 'test'
}

function setPaymentModalContext(scope: Scope, context: PaymentModalTelemetryContext = {}): void {
  const { level, tags, context: details } = paymentModalScope(context)
  scope.setLevel(level)
  scope.setTags(tags)
  scope.setContext('payment_modal', details)
}

export function reportPaymentModalFailure(
  message: string,
  context: PaymentModalTelemetryContext = {}
): void {
  if (shouldSkipTelemetry()) {
    return
  }

  Sentry.withScope((scope) => {
    setPaymentModalContext(scope, {
      ...context,
      event: context.event ?? 'payment_modal_failure',
    })
    Sentry.captureMessage(message)
  })
}

export interface PaymentInitializationWatch {
  mark(stage: string | null | undefined): void
  complete(stage?: string | null): void
  cancel(): void
}

export function startPaymentInitializationWatch(
  context: PaymentModalTelemetryContext = {},
  {
    thresholdMs = PAYMENT_INITIALIZATION_SLOW_THRESHOLD_MS,
    now = () => Date.now(),
    report = reportPaymentModalFailure,
  }: { thresholdMs?: number; now?: () => number; report?: ReportPaymentModalFailure } = {}
): PaymentInitializationWatch {
  const startedAt = now()
  const milestones: Record<string, number> = {}
  let currentStage = context.initializationStage ?? 'started'
  let isFinished = false
  let hasReported = false

  const recordMilestone = (stage: string) => {
    currentStage = stage
    milestones[stage] = Math.max(0, Math.round(now() - startedAt))
  }

  recordMilestone(currentStage)

  const timeoutId = globalThis.setTimeout(() => {
    if (isFinished || hasReported) {
      return
    }

    hasReported = true
    const initializationElapsedMs = Math.max(0, Math.round(now() - startedAt))

    report('Payment initialization exceeded 10 seconds.', {
      ...context,
      event: 'payment_initialization_slow',
      failureCategory: 'initialization_timeout',
      issueCode: `${context.initializationKind ?? 'payment'}_initialization_slow`,
      initializationStage: currentStage,
      initializationElapsedMs,
      initializationMilestonesMs: { ...milestones },
    })
  }, thresholdMs)

  const finish = () => {
    if (isFinished) {
      return
    }

    isFinished = true
    globalThis.clearTimeout(timeoutId)
  }

  return {
    mark(stage: string | null | undefined) {
      if (!isFinished && stage) {
        recordMilestone(stage)
      }
    },
    complete(stage: string | null = null) {
      if (stage) {
        recordMilestone(stage)
      }

      finish()
    },
    cancel: finish,
  }
}
